import { observer } from 'mobx-react-lite';
import ReadOnlyRecipientWell from './ReadOnlyRecipientWell';
import ReadOnlyTruncatedRecipientWell from './ReadOnlyTruncatedRecipientWell';
import ReadOnlyRecipientViewState from '../store/schema/ReadOnlyRecipientViewState';
import sortSelfFirst from '../utils/sortSelfFirst';
import { BaseButton } from 'office-ui-fabric-react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { collapseRecipientButtonAriaLabel } from 'owa-locstrings/lib/strings/collapserecipientbuttonarialabel.locstring.json';
import { showAllRecipientButtonAriaLabel } from 'owa-locstrings/lib/strings/showallrecipientbuttonarialabel.locstring.json';
import { RecipientType } from 'owa-recipient-types/lib/types/RecipientType';
import RecipientCountsType from 'owa-service/lib/contract/RecipientCountsType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import hasMoreRecipientsOnServer from '../utils/hasMoreRecipientsOnServer';
import * as React from 'react';

const styles = require('./ReadOnlyCompactRecipientWell.scss');

export interface CompactRecipientWellState {
    isTruncated: boolean;
}

export interface CompactRecipientWellProps {
    toRecipients: ReadOnlyRecipientViewState[];
    ccRecipients: ReadOnlyRecipientViewState[];
    bccRecipients: ReadOnlyRecipientViewState[];
    noneTypeRecipients?: ReadOnlyRecipientViewState[];
    noneTypeRecipientCount?: number;
    toRecipientsWellLabelTextOverride?: string;
    ccRecipientsWellLabelTextOverride?: string;
    recipientCounts?: RecipientCountsType;
    getAllRecipientsHandler?: () => void;
    className?: string;
    hideTrailingComponentIfNoTruncation?: boolean;
    recipientPersonaRef?: (ref: HTMLElement) => void;
    onTruncatedStateChanged?: (newState: boolean) => void;
}

export default observer(function ReadOnlyCompactRecipientWell(props: CompactRecipientWellProps) {
    const chevronIconRef = React.useRef<BaseButton>();
    const shouldForceFocusOnChevron = React.useRef<boolean>();
    const [isTruncated, setIsTruncated] = React.useState(true);
    React.useEffect(() => {
        if (shouldForceFocusOnChevron.current && chevronIconRef.current) {
            chevronIconRef.current.focus();
            shouldForceFocusOnChevron.current = false;
        }
    });
    const onToggleIsTruncated = (e: React.MouseEvent<unknown>) => {
        const { getAllRecipientsHandler } = props;
        e.stopPropagation();
        shouldForceFocusOnChevron.current = true;
        if (isTruncated && getAllRecipientsHandler) {
            const moreRecipientsOnServer = hasMoreRecipientsOnServer_0();
            if (moreRecipientsOnServer) {
                getAllRecipientsHandler();
            }
        }
        setIsTruncated(!isTruncated);
        if (props.onTruncatedStateChanged) {
            props.onTruncatedStateChanged(isTruncated);
        }
    };
    const hasMoreRecipientsOnServer_0 = (): boolean => {
        const {
            toRecipients,
            ccRecipients,
            bccRecipients,
            recipientCounts,
            noneTypeRecipients,
            noneTypeRecipientCount,
        } = props;
        let hasMoreRecipients = hasMoreRecipientsOnServer(
            recipientCounts,
            toRecipients,
            ccRecipients,
            bccRecipients
        );
        if (!hasMoreRecipients && noneTypeRecipientCount) {
            hasMoreRecipients =
                noneTypeRecipients && noneTypeRecipients.length < noneTypeRecipientCount;
        }
        return hasMoreRecipients;
    };
    const createTrailingComponent = (isCollapsed: boolean) => {
        return (
            <BaseButton
                className={styles.chevron}
                ref={chevronIconRef}
                onClick={onToggleIsTruncated}
                iconProps={{
                    iconName: isCollapsed
                        ? ControlIcons.DoubleChevronDown
                        : ControlIcons.DoubleChevronUp,
                    styles: {
                        root: styles.icon,
                    },
                }}
                role="button"
                checked={!isCollapsed}
                ariaLabel={
                    isCollapsed
                        ? loc(showAllRecipientButtonAriaLabel)
                        : loc(collapseRecipientButtonAriaLabel)
                }
            />
        );
    };
    const renderReadOnlyRecipientWell = (
        recipientType: RecipientType,
        recipients: ReadOnlyRecipientViewState[],
        renderTrailingComponent: boolean,
        wellLabel?: string
    ) => {
        return (
            <ReadOnlyRecipientWell
                recipientType={recipientType}
                recipients={recipients}
                trailingComponent={
                    renderTrailingComponent
                        ? createTrailingComponent(false /* showChevronsDown */)
                        : null
                }
                className={props.className}
                wellLabelText={wellLabel}
                recipientPersonaRef={props.recipientPersonaRef}
            />
        );
    };
    const {
        toRecipients,
        ccRecipients,
        bccRecipients,
        recipientCounts,
        noneTypeRecipients,
        noneTypeRecipientCount,
        toRecipientsWellLabelTextOverride,
        ccRecipientsWellLabelTextOverride,
    } = props;
    if (!isTruncated) {
        const isCcWellShown = ccRecipients && ccRecipients.length > 0;
        const isBccWellShown = bccRecipients && bccRecipients.length > 0;
        return (
            <span>
                {renderReadOnlyRecipientWell(
                    RecipientType.To,
                    toRecipients,
                    !isCcWellShown && !isBccWellShown,
                    toRecipientsWellLabelTextOverride
                )}
                {isCcWellShown &&
                    renderReadOnlyRecipientWell(
                        RecipientType.Cc,
                        ccRecipients,
                        !isBccWellShown,
                        ccRecipientsWellLabelTextOverride
                    )}
                {isBccWellShown &&
                    renderReadOnlyRecipientWell(RecipientType.Bcc, bccRecipients, true)}
                {noneTypeRecipients?.length &&
                    renderReadOnlyRecipientWell(RecipientType.None, noneTypeRecipients, true)}
            </span>
        );
    } else {
        let allRecipients: ReadOnlyRecipientViewState[] = [];
        if (isConsumer()) {
            allRecipients = sortSelfFirst(
                getAllRecipients(toRecipients, ccRecipients, bccRecipients, noneTypeRecipients)
            );
        } else {
            allRecipients = getAllRecipients(
                toRecipients,
                ccRecipients,
                bccRecipients,
                noneTypeRecipients
            );
        }
        let totalRecipientCount;
        if (recipientCounts || noneTypeRecipientCount) {
            totalRecipientCount =
                convertNullOrUndefinedToZero(recipientCounts?.ToRecipientsCount) +
                convertNullOrUndefinedToZero(recipientCounts?.CcRecipientsCount) +
                convertNullOrUndefinedToZero(recipientCounts?.BccRecipientsCount) +
                convertNullOrUndefinedToZero(noneTypeRecipientCount);
        } else {
            totalRecipientCount =
                convertNullOrUndefinedToZero(toRecipients?.length) +
                convertNullOrUndefinedToZero(ccRecipients?.length) +
                convertNullOrUndefinedToZero(bccRecipients?.length) +
                convertNullOrUndefinedToZero(noneTypeRecipients?.length);
        }
        const trailingComponent = createTrailingComponent(true /* showChevronsDown */);
        return (
            <ReadOnlyTruncatedRecipientWell
                recipientType={RecipientType.None}
                recipients={allRecipients}
                trailingComponent={trailingComponent}
                totalRecipientCount={totalRecipientCount}
                onToggleIsTruncated={onToggleIsTruncated}
                renderSelf={isConsumer()}
                className={props.className}
                hideTrailingComponentIfNoTruncation={props.hideTrailingComponentIfNoTruncation}
            />
        );
    }
});

function mergeRecipients(
    sourceArray: ReadOnlyRecipientViewState[],
    targetArray: ReadOnlyRecipientViewState[]
) {
    if (sourceArray) {
        for (const recipient of sourceArray) {
            targetArray.push(recipient);
        }
    }
}

function convertNullOrUndefinedToZero(n: number): number {
    return n ? n : 0;
}

function getAllRecipients(
    toRecipients: ReadOnlyRecipientViewState[],
    ccRecipients: ReadOnlyRecipientViewState[],
    bccRecipients: ReadOnlyRecipientViewState[],
    noneTypeRecipients?: ReadOnlyRecipientViewState[]
): ReadOnlyRecipientViewState[] {
    const totalRecipients = [];
    mergeRecipients(toRecipients, totalRecipients);
    mergeRecipients(ccRecipients, totalRecipients);
    mergeRecipients(bccRecipients, totalRecipients);
    mergeRecipients(noneTypeRecipients, totalRecipients);
    return totalRecipients;
}
